import { Component, signal } from '@angular/core';
import { Router } from '@angular/router';
import { DesyAngularModule } from 'desy-angular';
import { NewAccessionData } from '@/app/interfaces/new-accession-data';
import { ProtocolDetailsComponent } from './protocol-details/protocol-details.component';
import { BackButtonComponent } from '@/app/shared/back-button/back-button.component';
import { ProtocolDetailsResponse } from '@/app/interfaces/protocol-details';
import { ProtocolsService } from '@/app/services/protocols.service';
import { Step1Component } from './steps/step1/step1.component';
import { Step2Component } from './steps/step2/step2.component';
import { Step3Component } from './steps/step3/step3.component';
import { Step4Component } from './steps/step4/step4.component';
import { Step5Component } from './steps/step5/step5.component';
import { StepperComponent } from '@/app/shared/stepper/stepper.component';
import { Step3Form } from '@/app/interfaces/step3-form';
import { environment } from '@/environments/environment';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';

@Component({
  selector: 'app-new-accession',
  imports: [
    ConfirmDialog,
    DesyAngularModule,
    ProtocolDetailsComponent,
    Step1Component,
    Step2Component,
    Step3Component,
    Step4Component,
    Step5Component,
    StepperComponent,
    BackButtonComponent,
  ],
  templateUrl: './new-accession.component.html',
  providers: [ConfirmationService],
})
export class NewAccessionComponent {
  constructor(
    private router: Router,
    private protocolsService: ProtocolsService,
    private confirmationService: ConfirmationService
  ) {}

  newAccessionData: NewAccessionData = {
    name: '',
    protocol: null,
    service: null,
    rpts: [],
    observation: null,
    effectiveDate: null,
    title: null,
    type: null,
    range: null,
    destination: null,
    date: null,
    url: null,
  };

  steps = signal([
    { value: 1, label: 'Seleccionar protocolo', disabled: false },
    { value: 2, label: 'Seleccionar servicio', disabled: true },
    { value: 3, label: 'Completar formulario', disabled: true },
    { value: 4, label: 'Seleccionar rpts', disabled: true },
    { value: 5, label: 'Finalizar', disabled: true },
  ]);
  currentView = signal(this.steps()[0].value);
  protocolName = signal<string>('');
  isFirstTimeOnStep3 = signal<number>(0);
  isFirstTimeOnStep4 = signal<number>(0);
  isFirstTimeOnStep5 = signal<number>(0);
  serviceName = signal<string>('');
  currentDate = signal<number | null>(
    (() => {
      const d = new Date();
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return parseInt(`${yyyy}${mm}${dd}`, 10);
    })()
  );
  step3FormData: Partial<Step3Form> | null = null;
  protocolSelected = signal<ProtocolDetailsResponse>(null);
  jobcode = JSON.parse(localStorage.getItem('userData') || '{}')?.roleCode;

  async goViewProtocolDetails(protocolId: number) {
    if (!protocolId) return;
    const data = await this.protocolsService.getProtocolDetails(protocolId, 'protocol');
    this.protocolSelected.set(data);
    this.onStepSelected(6);
  }

  confirmGoStep(value) {
    this.confirmationService.confirm({
      message: 'Los datos modificados no se guardarán. ¿Está seguro/a de que desea continuar?',
      header: 'Confirmación',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Continuar',
      },
      accept: () => {
        this.onStepSelected(value);
      },
    });
  }

  onStepConfirm(e) {
    if (e.isClick && (e.stepCurrent === 3 || e.stepCurrent === 4)) {
      this.confirmGoStep(e.value);
    } else {
      this.onStepSelected(e.value);
    }
  }

  onStepSelected(stepValue) {
    this.currentView.set(stepValue);
  }

  onStep1Completed(e) {
    this.newAccessionData.protocol = e;
    this.protocolName.set(e.protocolName);
    this.newAccessionData.name = `${environment.environment}Adhesión - ${this.currentDate()} - ${this.protocolName()} - ${this.serviceName()} - ${this.jobcode}`;
    this.setStepDisabled(1, false);
    this.onStepSelected(2);
  }

  onStep2Completed(e) {
    this.newAccessionData.service = e;
    this.serviceName.set(e.organismName);
    this.newAccessionData.name = `${environment.environment}Adhesión - ${this.currentDate()} - ${this.protocolName()} - ${this.serviceName()} - ${this.jobcode}`;
    this.isFirstTimeOnStep3.update((v) => v + 1);
    this.setStepDisabled(2, false);
    this.onStepSelected(3);
  }

  formIsValid(isValid: boolean) {
    if (isValid) {
      if (this.isFirstTimeOnStep4() > 0) {
        this.setStepDisabled(3, false);
      }
      if (this.isFirstTimeOnStep5() > 0) {
        this.setStepDisabled(4, false);
      }
    } else {
      this.setStepDisabled(3, true);
      this.setStepDisabled(4, true);
    }
  }

  onStep4ValidityChange(isValid: boolean) {
    if (isValid) {
      if (this.isFirstTimeOnStep5() > 0) {
        this.setStepDisabled(4, false);
      }
    } else {
      this.setStepDisabled(4, true);
    }
  }

  onStep3Completed(data) {
    this.isFirstTimeOnStep4.update((v) => v + 1);
    this.newAccessionData.url = data.url;
    this.newAccessionData.range = data.range;
    this.newAccessionData.title = data.title;
    this.newAccessionData.type = data.type;
    this.newAccessionData.date = data.date;
    this.newAccessionData.effectiveDate = data.effectiveDate;

    this.setStepDisabled(3, false);
    this.currentView.set(4);
  }

  onStep4Completed(rpts) {
    this.isFirstTimeOnStep5.update((v) => v + 1);
    // this.newAccessionData.rpts = rpts.map((e) => e.jobCode);
    this.newAccessionData.rpts = rpts;
    this.setStepDisabled(4, false);
    this.currentView.set(5);
  }

  setStepDisabled(stepIndex: number, disabled: boolean) {
    this.steps.update((currentSteps) => {
      const updatedSteps = [...currentSteps];
      updatedSteps[stepIndex] = {
        ...updatedSteps[stepIndex],
        disabled,
      };
      return updatedSteps;
    });
  }
}
